import {
  type AerieGateEvent,
  type AerieMode,
  type AerieRejected,
  RAID_MAX_PLAYERS,
  RAID_MIN_PLAYERS,
  SHEFIRA_AERIE_NAME,
} from '@shefira/shared';

import './aerie.css';
import { aerieRefusalText, gateView } from './aerie-text.js';
import { closeButton } from './hud.js';
import { kitTitle } from './kit.js';
import { uiFormat, uiText } from './text.js';

/**
 * The window at the crevasse of La Brèche (ADR 0068 §4, §5, ADR 0071):
 * whether the story opens to this player, and the raid as the server sees
 * it, raider by raider, with what each still lacks. Its two buttons only ask
 * (`aerie_enter`); the server checks everything again and sends everyone
 * in, or says why not. Closed by the cross, by Escape, or by walking away.
 */
export class AerieGatePanel {
  private readonly root: HTMLElement;
  private readonly storyNote: HTMLElement;
  private readonly storyButton: HTMLButtonElement;
  private readonly raidNote: HTMLElement;
  private readonly list: HTMLElement;
  private readonly raidButton: HTMLButtonElement;
  private readonly message: HTMLElement;
  /** Between a click and the server's answer (the zone change, or a refusal). */
  private pending = false;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !this.root.hidden) this.close();
  };

  constructor(private readonly onEnter: (mode: AerieMode) => void) {
    this.root = document.createElement('section');
    this.root.className = 'aerie-gate';
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-label', uiText(SHEFIRA_AERIE_NAME));

    const title = kitTitle(document.createElement('h2'), 'stat-bosses');
    title.className = `${title.className} aerie-gate__title`;
    title.textContent = uiText(SHEFIRA_AERIE_NAME);
    title.append(
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );

    const story = document.createElement('div');
    story.className = 'aerie-gate__block';
    const storyHead = document.createElement('h3');
    storyHead.className = 'aerie-gate__heading';
    storyHead.textContent = uiText('La dernière garde');
    this.storyNote = document.createElement('p');
    this.storyNote.className = 'aerie-gate__note';
    this.storyButton = this.button('Entrer seul (histoire)', 'story');
    story.append(storyHead, this.storyNote, this.storyButton);

    const raid = document.createElement('div');
    raid.className = 'aerie-gate__block aerie-gate__block--raid';
    const raidHead = document.createElement('h3');
    raidHead.className = 'aerie-gate__heading';
    raidHead.textContent = uiFormat('Raid ({min} à {max} joueurs, niveau 20)', {
      min: RAID_MIN_PLAYERS,
      max: RAID_MAX_PLAYERS,
    });
    this.list = document.createElement('ul');
    this.list.className = 'aerie-gate__list';
    this.raidNote = document.createElement('p');
    this.raidNote.className = 'aerie-gate__note';
    this.raidButton = this.button('Lancer le raid', 'raid');
    raid.append(raidHead, this.list, this.raidNote, this.raidButton);

    this.message = document.createElement('p');
    this.message.className = 'aerie-gate__message';
    this.message.setAttribute('aria-live', 'polite');

    this.root.append(title, story, raid, this.message);
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKey);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  /** Opened while the server is asked: the buttons wait for its answer. */
  open(): void {
    this.root.hidden = false;
    this.pending = false;
    this.message.textContent = '';
    this.storyNote.textContent = uiText('La crevasse s’ouvre sous tes pieds…');
    this.raidNote.textContent = '';
    this.list.replaceChildren();
    this.storyButton.disabled = true;
    this.raidButton.disabled = true;
  }

  close(): void {
    this.root.hidden = true;
  }

  /** The server's answer to `aerie_status`. */
  setGate(gate: AerieGateEvent): void {
    const view = gateView(gate);
    this.storyNote.textContent = view.story.note;
    this.storyButton.disabled = this.pending || !view.story.open;
    this.list.replaceChildren(
      ...view.raid.rows.map((row) => {
        const item = document.createElement('li');
        item.className = 'aerie-gate__row';
        item.dataset.ready = String(row.ready);
        const name = document.createElement('span');
        name.className = 'aerie-gate__name';
        name.textContent = row.name;
        name.title = row.name;
        const level = document.createElement('span');
        level.className = 'aerie-gate__level';
        level.textContent = row.level;
        const note = document.createElement('span');
        note.className = 'aerie-gate__state';
        note.textContent = row.note;
        item.append(name, level, note);
        return item;
      }),
    );
    this.list.hidden = view.raid.rows.length === 0;
    this.raidNote.textContent = view.raid.note;
    this.raidButton.disabled = this.pending || !view.raid.canLaunch;
  }

  showRejected(rejected: AerieRejected): void {
    this.pending = false;
    this.message.textContent = aerieRefusalText(rejected);
    this.message.dataset.tone = 'refused';
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }

  private button(label: string, mode: AerieMode): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'aerie-gate__go';
    button.textContent = uiText(label);
    button.addEventListener('click', () => {
      if (this.pending) return;
      this.pending = true;
      this.storyButton.disabled = true;
      this.raidButton.disabled = true;
      this.message.textContent = '';
      this.onEnter(mode);
    });
    return button;
  }
}
